import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {ArrowRight, ChevronRight, Home} from 'lucide-react';

export default function ServiceDetailPage() {
  const { serviceId } = useParams<{ serviceId: string }>();

  const serviceDetails: Record<string, any> = {
    'dot-specimen': {
      name: 'DOT & Non-DOT Specimen Collection',
      icon: '🧪',
      description: 'Professional specimen collection services ensuring compliance with Department of Transportation regulations and industry standards.',
      fullDescription: 'Our certified collectors follow strict protocols for both DOT-regulated and non-DOT specimen collection. We maintain complete chain of custody documentation and ensure all samples are handled with the highest level of integrity and professionalism.',
      benefits: [
        'DOT-qualified collection professionals',
        'Strict chain of custody procedures',
        'Immediate sample processing and transport',
        'Nationwide testing network access',
        'Same-day and next-day results available',
        'Comprehensive reporting and documentation'
      ],
      process: [
        'Schedule your appointment online or by phone',
        'Arrive at our facility with valid photo ID',
        'Complete necessary paperwork and consent forms',
        'Sample collection by certified professional',
        'Immediate processing and secure transport',
        'Receive results within specified timeframe'
      ],
      pricing: 'Competitive pricing based on specific testing requirements. Contact us for a detailed quote.',
      turnaround: '24-48 hours for standard tests, expedited options available'
    },
    'dna-testing': {
      name: 'DNA Testing',
      icon: '🧬',
      description: 'Accurate and confidential DNA testing services for legal, immigration, and personal identification purposes.',
      fullDescription: 'Our DNA testing services utilize state-of-the-art technology to provide accurate results for paternity, relationship testing, and legal proceedings. All tests are performed in AABB-accredited laboratories with strict quality controls.',
      benefits: [
        'AABB-accredited laboratory testing',
        '99.9% accuracy guarantee',
        'Legal and court-admissible results',
        'Confidential and secure processing',
        'Immigration DNA testing available',
        'Same-day sample collection'
      ],
      process: [
        'Choose your testing type and schedule appointment',
        'Visit our facility with all parties and photo IDs',
        'Witness verification and documentation',
        'Non-invasive cheek swab sample collection',
        'Samples sent to accredited laboratory',
        'Receive comprehensive report within 3-5 business days'
      ],
      pricing: 'Starting at $299 for standard paternity testing. Legal testing priced separately.',
      turnaround: '3-5 business days for standard testing'
    },
    'blood-testing': {
      name: 'Blood Testing',
      icon: '💉',
      description: 'Comprehensive blood analysis including routine panels, specialized tests, and health screenings.',
      fullDescription: 'Our blood testing services cover a wide range of diagnostic needs, from routine health screenings to specialized disease markers. All tests are performed by experienced phlebotomists in a comfortable environment.',
      benefits: [
        'Experienced certified phlebotomists',
        'Comprehensive test menu',
        'Fast and accurate results',
        'Comfortable collection environment',
        'Electronic results delivery',
        'Direct physician reporting available'
      ],
      process: [
        'Obtain test order from your physician',
        'Schedule appointment or walk-in',
        'Fasting requirements (if applicable) communicated in advance',
        'Quick and professional blood draw',
        'Samples processed in certified laboratory',
        'Results sent to you and your physician'
      ],
      pricing: 'Varies by test panel. Most insurance plans accepted.',
      turnaround: '1-3 business days for most standard tests'
    },
    'urine-drug-testing': {
      name: 'Urine Drug Testing',
      icon: '🔬',
      description: 'Reliable drug screening for employment, legal, probation, and personal purposes with rapid results.',
      fullDescription: 'Our urine drug testing services meet all federal guidelines and industry standards. We offer both instant screening and laboratory confirmation testing with complete chain of custody.',
      benefits: [
        'DOT and Non-DOT testing available',
        '5-panel to 12-panel test options',
        'Instant screening results available',
        'Laboratory confirmation testing',
        'Complete chain of custody',
        'MRO review services available'
      ],
      process: [
        'Schedule appointment or walk-in',
        'Present valid photo identification',
        'Complete donor paperwork',
        'Observed or non-observed collection',
        'Instant results or lab confirmation',
        'Secure electronic result delivery'
      ],
      pricing: 'Starting at $45 for instant screening, lab confirmation additional.',
      turnaround: 'Instant results for screening, 24-48 hours for confirmation'
    },
    'flu-testing': {
      name: 'Flu Testing',
      icon: '🦠',
      description: 'Rapid influenza diagnostic testing for quick identification and treatment guidance.',
      fullDescription: 'Our rapid flu tests provide accurate results in minutes, helping you and your healthcare provider make informed treatment decisions quickly during flu season.',
      benefits: [
        'Results in 15 minutes',
        'Detects Influenza A and B',
        'High accuracy rates',
        'Walk-in appointments welcome',
        'Comfortable nasal swab collection',
        'Immediate treatment guidance'
      ],
      process: [
        'Walk-in or schedule quick appointment',
        'Brief symptom assessment',
        'Simple nasal swab collection',
        'Wait 15 minutes for results',
        'Receive results and treatment recommendations',
        'Results forwarded to your physician if needed'
      ],
      pricing: '$60 for rapid flu test',
      turnaround: '15 minutes'
    },
    'fingerprint': {
      name: 'Electronic Fingerprint',
      icon: '👆',
      description: 'Digital fingerprinting for FBI background checks, employment, licensing, and legal requirements.',
      fullDescription: 'Our electronic fingerprinting services provide fast, accurate digital captures for all state and federal background check requirements. We serve individuals, employers, and agencies.',
      benefits: [
        'FBI-approved channeler',
        'State and federal submissions',
        'Digital capture technology',
        'No ink or mess',
        'Immediate electronic submission',
        'Expedited processing available'
      ],
      process: [
        'Schedule fingerprinting appointment',
        'Bring required documentation and codes',
        'Digital fingerprint capture (2-3 minutes)',
        'Electronic submission to appropriate agency',
        'Receive confirmation receipt',
        'Background check results sent by requesting agency'
      ],
      pricing: 'Starting at $50 for standard FBI background check',
      turnaround: 'Results timing varies by requesting agency (typically 3-5 business days)'
    },
    'i9-checks': {
      name: 'I-9 Background Checks',
      icon: '📋',
      description: 'Employment eligibility verification and comprehensive background screening services.',
      fullDescription: 'Our I-9 verification and background check services help employers maintain compliance with federal employment laws while making informed hiring decisions.',
      benefits: [
        'I-9 form completion assistance',
        'E-Verify services',
        'Comprehensive background screening',
        'Criminal record checks',
        'Employment history verification',
        'Education credential verification'
      ],
      process: [
        'Employer submits screening request',
        'Candidate completes consent forms',
        'In-person document verification',
        'Background checks initiated',
        'Comprehensive report compiled',
        'Results delivered to employer'
      ],
      pricing: 'Custom packages available based on screening requirements',
      turnaround: '3-7 business days depending on scope'
    },
    'ctpa': {
      name: 'C/TPA Services',
      icon: '🏥',
      description: 'Consortium/Third Party Administrator services for comprehensive drug and alcohol testing program management.',
      fullDescription: 'Our C/TPA services provide complete management of your drug and alcohol testing program, ensuring DOT compliance and efficient administration of random testing pools.',
      benefits: [
        'DOT compliance management',
        'Random testing pool administration',
        'Policy development support',
        'Supervisor training',
        'Annual reporting',
        'MRO coordination'
      ],
      process: [
        'Initial consultation and program setup',
        'Employee enrollment in testing pool',
        'Random selection notifications',
        'Test coordination and scheduling',
        'Results management and reporting',
        'Ongoing compliance support'
      ],
      pricing: 'Annual service fees based on company size and testing frequency',
      turnaround: 'Ongoing program management with immediate response to testing needs'
    }
  };

  const service = serviceDetails[serviceId || ''] || serviceDetails['dot-specimen'];

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="bg-gray-50 py-4 px-4 border-b border-gray-200" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-600 hover:text-[#4AB8D8] transition-colors flex items-center gap-1">
            <Home className="w-4 h-4" aria-hidden="true" />
            Home
          </Link>
          <ChevronRight className="w-4 h-4 text-gray-400" aria-hidden="true" />
          <Link to="/services" className="text-gray-600 hover:text-[#4AB8D8] transition-colors">
            Services
          </Link>
          <ChevronRight className="w-4 h-4 text-gray-400" aria-hidden="true" />
          <span className="text-gray-900 font-medium">{service.name}</span>
        </div>
      </nav>

      {/* Header */}
      <section className="bg-gradient-to-r from-[#4AB8D8] to-[#3A9AB8] text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-6xl mb-6" aria-hidden="true">{service.icon}</div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{service.name}</h1>
          <p className="text-xl text-white/90 max-w-3xl">{service.description}</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-12">
            {/* Featured Image for DOT Specimen */}
            {serviceId === 'dot-specimen' && (
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://images.pexels.com/photos/3825529/pexels-photo-3825529.jpeg?auto=compress&cs=tinysrgb&w=1200" 
                  alt="Diverse team of Medical Laboratory professionals in modern diagnostic facility"
                  className="w-full h-96 object-cover"
                />
              </div>
            )}
            
            {/* Overview */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Overview</h2>
              <p className="text-gray-700 text-lg leading-relaxed">{service.fullDescription}</p>
            </div>

            {/* Benefits */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Benefits & Features</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {service.benefits.map((benefit: string, index: number) => (
                  <div key={index} className="flex items-start gap-3 bg-[#4AB8D8]/10 p-4 rounded-lg">
                    <span className="text-[#F8A356] text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-800">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Process */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Testing Process</h2>
              <div className="space-y-4">
                {service.process.map((step: string, index: number) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#4AB8D8] text-white rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <p className="text-gray-700 pt-1">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pricing Card */}
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Pricing</h3>
              <p className="text-gray-700 mb-6">{service.pricing}</p>
              <h4 className="font-semibold text-gray-900 mb-2">Turnaround Time</h4>
              <p className="text-gray-700 mb-6">{service.turnaround}</p>
              <Link
                to="/appointment"
                className="block w-full text-center bg-[#4AB8D8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#F8A356] transition-colors"
              >
                Book Appointment
              </Link>
            </div>

            {/* Contact Card */}
            <div className="bg-[#4AB8D8] text-white p-6 rounded-xl">
              <h3 className="text-xl font-bold mb-4">Have Questions?</h3>
              <p className="mb-6">Our team is ready to help you understand your testing options.</p>
              <a
                href="tel:9842309599"
                className="block w-full text-center bg-white text-[#4AB8D8] px-6 py-3 rounded-lg font-semibold hover:bg-[#F8A356] hover:text-white transition-colors mb-3"
              >
                Call (984) 230-9599
              </a>
              <Link
                to="/contact"
                className="block w-full text-center bg-[#3A9AB8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#F8A356] transition-colors"
              >
                Contact Us
              </Link>
            </div>

            {/* Related Services */}
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Related Services</h3>
              <Link to="/services" className="flex items-center justify-between text-[#4AB8D8] hover:text-[#F8A356] font-medium">
                View All Services <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
